import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff, AlertTriangle } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_culinary-connect-37/artifacts/dowryrls_582627c1-c0bf-47f6-ab36-5cc8b78702e3.png";

const PW_MIN = 8;

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function calcStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pw.length >= PW_MIN) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: "Weak", color: "bg-red-500" };
  if (score === 2) return { score, label: "Fair", color: "bg-amber-500" };
  if (score === 3) return { score, label: "Good", color: "bg-emerald-500" };
  return { score, label: "Strong", color: "bg-emerald-400" };
}

function PasswordChecks({ pw }) {
  const checks = [
    { ok: pw.length >= PW_MIN, label: `At least ${PW_MIN} characters` },
    { ok: /[A-Za-z]/.test(pw), label: "At least one letter" },
    { ok: /\d/.test(pw), label: "At least one number" },
  ];
  return (
    <ul className="mt-2 space-y-1" data-testid="password-checks">
      {checks.map(c => (
        <li key={c.label} className={`text-xs flex items-center gap-2 transition-colors ${c.ok ? "text-emerald-400" : "text-[#A0A0A0]"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${c.ok ? "bg-emerald-400" : "bg-white/25"}`} />
          {c.label}
        </li>
      ))}
    </ul>
  );
}

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, register } = useAuth();
  const isLogin = location.pathname === "/login";
  const searchParams = new URLSearchParams(location.search);
  const defaultRole = searchParams.get("role") || "client";

  const [mode, setMode] = useState(isLogin ? "login" : "register");
  const [role, setRole] = useState(defaultRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "chef") navigate("/chef/dashboard");
      else navigate("/find-a-chef");
    }
  }, [user, navigate]);

  // Reset attempts counter when user switches mode or edits email
  useEffect(() => { setFailedAttempts(0); }, [mode, email]);

  const strength = useMemo(() => calcStrength(password), [password]);

  const trimmedPw = password.trim();
  const pwValid = trimmedPw.length >= PW_MIN && /[A-Za-z]/.test(trimmedPw) && /\d/.test(trimmedPw);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register") {
      if (trimmedPw !== confirm.trim()) {
        setError("Passwords do not match");
        return;
      }
      if (!pwValid) {
        setError(`Password must be at least ${PW_MIN} characters and include both a letter and a number.`);
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(email.trim(), password);
        setFailedAttempts(0);
        if (u.role === "admin") navigate("/admin");
        else if (u.role === "chef") navigate("/chef/dashboard");
        else navigate("/find-a-chef");
      } else {
        const u = await register(name, email.trim(), password, role);
        if (u.role === "chef") navigate("/chef/dashboard");
        else navigate("/find-a-chef");
      }
    } catch (err) {
      if (mode === "login" && err.response?.status === 401) {
        setFailedAttempts(n => n + 1);
      }
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-20 pb-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/">
            <img src={LOGO} alt="Are You A Chef" className="h-16 w-16 rounded-full mx-auto mb-4" />
          </Link>
          <h1 className="font-serif text-3xl text-white font-light">
            {mode === "login" ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-[#A0A0A0] text-sm mt-2">
            {mode === "login" ? "Sign in to your account" : "Join the UK's premier chef directory"}
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="flex border border-[#D4AF37]/20 mb-8">
          <button
            onClick={() => setMode("login")}
            data-testid="auth-login-tab"
            className={`flex-1 py-3 text-sm tracking-wide transition-colors ${mode === "login" ? "bg-[#D4AF37] text-black font-medium" : "text-[#A0A0A0] hover:text-white"}`}
          >
            Login
          </button>
          <button
            onClick={() => setMode("register")}
            data-testid="auth-register-tab"
            className={`flex-1 py-3 text-sm tracking-wide transition-colors ${mode === "register" ? "bg-[#D4AF37] text-black font-medium" : "text-[#A0A0A0] hover:text-white"}`}
          >
            Register
          </button>
        </div>

        <form onSubmit={handleSubmit} data-testid="auth-form" className="space-y-6">
          {mode === "register" && (
            <>
              {/* Role Selection */}
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">I am registering as a</p>
                <div className="grid grid-cols-2 gap-3">
                  {["client", "chef"].map(r => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`role-${r}-btn`}
                      onClick={() => setRole(r)}
                      className={`py-3 px-4 border text-sm tracking-wide transition-all duration-200 ${
                        role === r
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                          : "border-white/15 text-[#A0A0A0] hover:border-[#D4AF37]/40"
                      }`}
                    >
                      {r === "client" ? "Client" : "Private Chef"}
                      <span className="block text-xs opacity-60 mt-0.5">
                        {r === "client" ? "Hire a chef" : "List services"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Full Name</label>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  placeholder="Your full name"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white placeholder-white/25 text-sm outline-none transition-colors"
                />
              </div>
            </>
          )}

          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Email Address</label>
            <input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              autoComplete={mode === "login" ? "email" : "email"}
              className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white placeholder-white/25 text-sm outline-none transition-colors"
            />
          </div>

          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Password</label>
            <div className="relative">
              <input
                data-testid="auth-password-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder={mode === "register" ? `At least ${PW_MIN} characters` : "••••••••"}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 pr-10 text-white placeholder-white/25 text-sm outline-none transition-colors"
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#D4AF37] transition-colors p-1"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {mode === "register" && (
              <>
                {password && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-1 bg-white/10 overflow-hidden">
                        <div
                          data-testid="password-strength-bar"
                          className={`h-full transition-all duration-300 ${strength.color}`}
                          style={{ width: `${(strength.score / 5) * 100}%` }}
                        />
                      </div>
                      <span data-testid="password-strength-label" className="text-xs text-[#A0A0A0] w-14 text-right">{strength.label}</span>
                    </div>
                  </div>
                )}
                <PasswordChecks pw={password} />
                <p className="text-xs text-[#555] mt-2">
                  Passwords are case-sensitive. Leading/trailing spaces are automatically removed.
                </p>
              </>
            )}
          </div>

          {mode === "register" && (
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Confirm Password</label>
              <div className="relative">
                <input
                  data-testid="auth-confirm-input"
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 pr-10 text-white placeholder-white/25 text-sm outline-none transition-colors"
                />
                <button
                  type="button"
                  data-testid="toggle-confirm-visibility"
                  onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#D4AF37] transition-colors p-1"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {confirm && confirm.trim() !== password.trim() && (
                <p className="text-red-400 text-xs mt-1.5">Passwords do not match.</p>
              )}
            </div>
          )}

          {error && (
            <div data-testid="auth-error" className="text-red-400 text-sm bg-red-900/20 border border-red-900/30 px-4 py-3 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {mode === "login" && failedAttempts >= 2 && (
            <div data-testid="forgot-password-hint" className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-3 text-sm">
              <p className="text-[#D4AF37] mb-1">Trouble signing in?</p>
              <p className="text-[#A0A0A0] text-xs mb-2">
                Passwords are case-sensitive. Check for leading/trailing spaces from autofill or copy-paste.
              </p>
              <Link to="/forgot-password" className="text-[#D4AF37] text-sm underline underline-offset-2 hover:text-[#F3E5AB]">
                Reset your password
              </Link>
            </div>
          )}

          <button
            type="submit"
            data-testid="auth-submit-btn"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] py-4 font-medium tracking-widest uppercase text-sm transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
          </button>

          {mode === "login" && (
            <div className="text-center">
              <Link to="/forgot-password" className="text-[#A0A0A0] hover:text-[#D4AF37] text-sm transition-colors">
                Forgot your password?
              </Link>
            </div>
          )}
        </form>

        {mode === "register" && role === "chef" && (
          <div className="mt-6 border border-[#D4AF37]/20 p-4 text-center">
            <p className="text-[#A0A0A0] text-xs leading-relaxed">
              Chef listing requires a quarterly payment of <span className="text-[#D4AF37]">£40</span>.
              You&apos;ll be guided through this after creating your account.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
